// mcn-frontend/src/components/layout/Footer.jsx
//
// Impressive dark/slate footer with purple-gradient brand mark, CMS-driven
// link sections + social icons, and a stats strip at the bottom.
//
// Data shape (footer.json):
//   logo       { primaryText, accentText }
//   description string
//   sections   [{ title, links: [{ label, href, external?, isButton? }] }]
//   social     [{ name, url, iconPath }]   iconPath is raw <path .../> markup
//   contact    { title, email, phone, location }
//   copyright  string
//   stats      [string]   optional — falls back to a sensible default
//
// All fields have hardcoded fallbacks so the footer never renders blank.
import React from 'react';
import { Link } from 'react-router-dom';
import './Footer.css';

// ---------- Fallback content ----------
const FALLBACK_SECTIONS = [
  {
    title: 'Platform',
    links: [
      { label: 'About Us', href: '/about' },
      { label: 'Our Channels', href: '/channels' },
      { label: 'For Creators', href: '/apply' },
      { label: 'Help Center', href: '/help' },
    ],
  },
  {
    title: 'Support',
    links: [
      { label: 'FAQ', href: '/faq' },
      { label: 'Contact Us', href: '/contact' },
      { label: 'Community Guidelines', href: '/guidelines' },
      { label: 'Help Center', href: '/help' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Terms of Service', href: '/terms' },
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Copyright Policy', href: '/copyright' },
    ],
  },
];

const FALLBACK_STATS = ['10 Channels', '156 Creators', '50M+ Views'];

const renderFooterLink = (link) => {
  const href = link.href || '#';
  const isExternal = link.external || /^https?:\/\//i.test(href) || href.startsWith('mailto:');
  const isAnchor = href.startsWith('#');

  if (isExternal) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {link.label}
      </a>
    );
  }
  if (isAnchor) {
    // Anchor — fine as a plain <a>. On non-homepage routes this becomes a
    // no-op, which is acceptable for an in-page hash.
    return <a href={href}>{link.label}</a>;
  }
  // Internal route — use Router Link for SPA navigation.
  return <Link to={href}>{link.label}</Link>;
};

const Footer = ({ footerData }) => {
  const logo = footerData?.logo || { primaryText: 'Choice', accentText: 'MCN' };
  const description =
    footerData?.description ||
    "Empowering creators worldwide through transparent revenue sharing, investor-backed funding, and professional channel management.";
  const sections =
    Array.isArray(footerData?.sections) && footerData.sections.length > 0
      ? footerData.sections
      : FALLBACK_SECTIONS;
  const social = Array.isArray(footerData?.social) ? footerData.social : [];
  const contact = footerData?.contact;
  const copyright =
    footerData?.copyright || `© ${new Date().getFullYear()} Choice Network MCN. All rights reserved.`;
  const stats = Array.isArray(footerData?.stats) && footerData.stats.length > 0
    ? footerData.stats
    : FALLBACK_STATS;

  return (
    <footer className="app-footer">
      <div className="footer-container">
        {/* Brand column */}
        <div className="footer-section footer-brand">
          <h4 className="footer-brand-name">
            {logo.primaryText || 'Choice'} {logo.accentText || 'MCN'}
          </h4>
          <p className="footer-brand-tagline">{description}</p>

          {social.length > 0 && (
            <div className="footer-social">
              {social.map((s) => (
                <a
                  key={s.name}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.name}
                  className="footer-social-link"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                    dangerouslySetInnerHTML={{ __html: s.iconPath || '' }}
                  />
                </a>
              ))}
            </div>
          )}
        </div>

        {/* CMS-driven link sections */}
        {sections.map((section) => (
          <div className="footer-section" key={section.title}>
            <h5>{section.title}</h5>
            <ul>
              {(section.links || []).map((link) => (
                <li key={`${section.title}-${link.label}`}>
                  {renderFooterLink(link)}
                </li>
              ))}
            </ul>
          </div>
        ))}

        {/* Contact column (optional) */}
        {contact && (
          <div className="footer-section">
            <h5>{contact.title || 'Contact'}</h5>
            <ul className="footer-contact-list">
              {contact.email && (
                <li>
                  <a href={`mailto:${contact.email}`}>{contact.email}</a>
                </li>
              )}
              {contact.phone && <li>{contact.phone}</li>}
              {contact.location && <li>{contact.location}</li>}
            </ul>
          </div>
        )}
      </div>

      <div className="footer-bottom">
        <p>{copyright}</p>
        <div className="footer-stats">
          {stats.map((stat, idx) => (
            <React.Fragment key={stat}>
              <span>{stat}</span>
              {idx < stats.length - 1 && <span aria-hidden>•</span>}
            </React.Fragment>
          ))}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
